import React, { useState } from 'react';
import { FeatherChevronDown, FeatherUsers, FeatherFolder, FeatherAlertCircle } from '../ui/icons';
import { Avatar } from '../ui/avatar';
import './ResourcesTreeView.css';

const ResourcesTreeView = ({ resources, loading, error }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Generate initials from resource name
    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    if (loading) {
        return (
            <div className="resources-timeline">
                <div className="resources-timeline-header">
                    <div className="resources-timeline-title">
                        <span className="resources-timeline-icon"><FeatherUsers /></span>
                        <span>Resources & Projects Timeline</span>
                    </div>
                    <button
                        className="resources-timeline-toggle"
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        aria-label={isCollapsed ? "Expand" : "Collapse"}
                    >
                        <FeatherChevronDown className={isCollapsed ? 'collapsed' : ''} />
                    </button>
                </div>
                {!isCollapsed && (
                    <div className="resources-timeline-loading">
                        <div className="loading-spinner-small" />
                        <p>Loading resources...</p>
                    </div>
                )}
            </div>
        );
    }

    if (error) {
        return (
            <div className="resources-timeline">
                <div className="resources-timeline-header">
                    <div className="resources-timeline-title">
                        <span className="resources-timeline-icon"><FeatherUsers /></span>
                        <span>Resources & Projects Timeline</span>
                    </div>
                    <button
                        className="resources-timeline-toggle"
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        aria-label={isCollapsed ? "Expand" : "Collapse"}
                    >
                        <FeatherChevronDown className={isCollapsed ? 'collapsed' : ''} />
                    </button>
                </div>
                {!isCollapsed && (
                    <div className="resources-timeline-error">
                        <p>{error}</p>
                    </div>
                )}
            </div>
        );
    }

    if (!resources || resources.length === 0) {
        return (
            <div className="resources-timeline">
                <div className="resources-timeline-header">
                    <div className="resources-timeline-title">
                        <span className="resources-timeline-icon"><FeatherUsers /></span>
                        <span>Resources & Projects Timeline</span>
                    </div>
                    <button
                        className="resources-timeline-toggle"
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        aria-label={isCollapsed ? "Expand" : "Collapse"}
                    >
                        <FeatherChevronDown className={isCollapsed ? 'collapsed' : ''} />
                    </button>
                </div>
                {!isCollapsed && (
                    <div className="resources-timeline-empty">
                        <p>No resources found</p>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="resources-timeline">
            <div className="resources-timeline-header">
                <div className="resources-timeline-title">
                    <span className="resources-timeline-icon"><FeatherUsers /></span>
                    <span>Resources & Projects Timeline</span>
                </div>
                <button
                    className="resources-timeline-toggle"
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    aria-label={isCollapsed ? "Expand" : "Collapse"}
                >
                    <FeatherChevronDown className={isCollapsed ? 'collapsed' : ''} />
                </button>
            </div>
            {!isCollapsed && (
                <div className="resources-timeline-content">
                    <div className="resources-timeline-scroll">
                        {resources.map((resource, resourceIndex) => {
                            const hasProjects = resource.projects && resource.projects.length > 0;
                            const projectCount = resource.projects ? resource.projects.length : 0;
                            const resourceName = resource.display_name || resource.email;
                            const initials = getInitials(resourceName);

                            return (
                                <div
                                    key={resource.id}
                                    className={`resource-timeline-node ${!resource.hasWorkPlan ? 'no-work-plan' : ''}`}
                                >
                                    <div className={`resource-node-card ${resource.hasWorkPlan ? 'has-work-plan' : 'no-work-plan-card'}`}>
                                        <div className="resource-node-avatar">
                                            <Avatar 
                                                size="medium" 
                                                image={resource.photo_url}
                                                className="resource-avatar"
                                            >
                                                {initials}
                                            </Avatar>
                                        </div>
                                        <div className="resource-node-name">
                                            {resourceName}
                                        </div>
                                        {resource.hasWorkPlan ? (
                                            <div className="resource-node-stats">
                                                <span className="resource-stat-item">
                                                    {resource.totalTasks} {resource.totalTasks === 1 ? 'task' : 'tasks'}
                                                </span>
                                                <span className="resource-stat-separator">•</span>
                                                <span className="resource-stat-item">
                                                    {resource.totalHours}h
                                                </span>
                                                {projectCount > 0 && (
                                                    <>
                                                        <span className="resource-stat-separator">•</span>
                                                        <span className="resource-stat-item resource-stat-projects">
                                                            {projectCount} {projectCount === 1 ? 'project' : 'projects'}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="resource-node-no-plan">
                                                <span className="resource-alert-icon"><FeatherAlertCircle /></span>
                                                <span>No work plan</span>
                                            </div>
                                        )}
                                    </div>
                                    {hasProjects && (
                                        <>
                                            <div className="resource-pipe-connector"></div>
                                            <div className="resource-projects-pipes">
                                                {resource.projects.map((project, projectIndex) => (
                                                    <div
                                                        key={project.id}
                                                        className="project-pipe"
                                                    >
                                                        <div className="project-pipe-content">
                                                            <span className="project-pipe-icon"><FeatherFolder /></span>
                                                            <div className="project-pipe-info">
                                                                <div className="project-pipe-name">
                                                                    {project.name}
                                                                    {project.abbreviation && (
                                                                        <span className="project-pipe-abbr"> ({project.abbreviation})</span>
                                                                    )}
                                                                </div>
                                                                <div className="project-pipe-stats">
                                                                    {project.taskCount} {project.taskCount === 1 ? 'task' : 'tasks'}, {project.totalHours}h
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {projectIndex < resource.projects.length - 1 && (
                                                            <div className="project-pipe-separator"></div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResourcesTreeView;

